import type { ComponentType } from 'react'
import type { z } from 'zod'
import type { Edge, Resource } from '@/db/types'

// The client twin of the Go Kind interface (spec §4.1). `apply` must be
// byte-for-byte equivalent to the Go implementation; testdata/actions proves it.

export type KindErrorCode = 'conflict' | 'invalid'

export class KindError extends Error {
  constructor(
    public code: KindErrorCode,
    message: string,
  ) {
    super(message)
    this.name = 'KindError'
  }
}

export const conflict = (msg: string) => new KindError('conflict', msg)
export const invalid = (msg: string) => new KindError('invalid', msg)

export interface QuickAddResult<Spec> {
  spec: Partial<Spec>
  labels?: Record<string, string>
  /** project name mentioned with @ — resolved by the caller */
  project?: string
  /** workspace slug mentioned with + */
  workspace?: string
}

export interface ClientKind<Spec = Record<string, unknown>, Status = Record<string, unknown>> {
  name: string
  version: number
  spec: z.ZodType<Spec>
  status: z.ZodType<Status>
  defaults(spec: Partial<Spec>): Spec
  /** Same rules as Go Validate; edgesFrom are the live edges leaving the resource. */
  validate(r: Resource<Spec, Status>, edgesFrom: Edge[]): string[]
  initialStatus(r: Resource<Spec, Status>): Status
  displayName(r: Resource<Spec, Status>): string
  /** Optimistic local apply. Throws KindError on invalid transitions. */
  apply(r: Resource<Spec, Status>, action: string, args: unknown, at: string): Resource<Spec, Status>
  /** Hook after a user patch (mirrors Go PatchObserver). */
  afterPatch?(r: Resource<Spec, Status>, at: string): Resource<Spec, Status>
  actions: string[]
  /** true when every resource of this kind belongs to one workspace (label `ws`) */
  workspaceScoped?: boolean
  /** version → migration from that version to the next */
  migrations?: Record<number, (spec: Record<string, unknown>) => Record<string, unknown>>
  ListRow: ComponentType<{ resource: Resource<Spec, Status> }>
  Detail: ComponentType<{ resource: Resource<Spec, Status> }>
  Form: ComponentType<{ resource?: Resource<Spec, Status>; onDone: () => void }>
  quickAddParse?(text: string): QuickAddResult<Spec>
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyKind = ClientKind<any, any>

/** Drop undefined, null, '' and empty arrays so JSON matches Go's omitempty. */
export const compact = <T extends Record<string, unknown>>(obj: T): T => {
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined || v === null || v === '') continue
    if (Array.isArray(v) && v.length === 0) continue
    out[k] = v
  }
  return out as T
}

export class KindRegistry {
  private kinds = new Map<string, AnyKind>()
  register(k: AnyKind) {
    this.kinds.set(k.name, k)
    return this
  }
  get(name: string): AnyKind {
    const k = this.kinds.get(name)
    if (!k) throw invalid(`unknown kind ${name}`)
    return k
  }
  has(name: string) {
    return this.kinds.has(name)
  }
  all(): AnyKind[] {
    return [...this.kinds.values()]
  }
}

/** Migrate a spec forward to the kind's current version (for old local rows). */
export const migrateSpec = (k: AnyKind, version: number, spec: Record<string, unknown>) => {
  let out = spec
  for (let v = version; v < k.version; v++) {
    const m = k.migrations?.[v]
    if (!m) throw invalid(`${k.name}: no migration from v${v}`)
    out = m(out)
  }
  return out
}
